import { IS_DEV_BUILD, DEV_FRONTDOOR_BASE_URL, DEV_SSO_LOGIN_URL, DOGFOOD_SSO_LOGIN_URL, PROD_SSO_LOGIN_URL } from "../../AppConstants";
import { TranscriptionStorageType } from "../interfaces/IStoredTranscription";

export enum HostAppName {
  WORD = 'WORD',    // Word App
  ONENOTE = 'ONENOTE'    // OneNote App
}

export enum HostAppSettingName {
    AugLoopCloudALServiceUrl = 'AugLoopCloudALServiceUrl',
    IsProductionEnvironment = 'IsProductionEnvironment',
    TranscriptionSessionId = 'TranscriptionSessionId',
    MonthlyMinutesUsed = 'MonthlyMinutesUsed',
    HasStoredTranscription = 'HasStoredTranscription',
    StoredTranscription = 'StoredTranscription',
    IsNewXMLStorageEnabledForWord = 'EnableNewXMLStorageForWord',
    IsFreemiumUpsellEnabled = 'IsFreemiumUpsellEnabled',
    IsLicensedUser = 'IsLicensedUser',
    DefaultTranscriptionLocale = 'DefaultTranscriptionLocale',
    FrontDoorBaseUrl = 'FrontDoorBaseUrl',
    EnableSetAnnotationsFromALVoiceClient = 'EnableSetAnnotationsFromALVoiceClient',
    UnlimitedMins = 'UnlimitedMins',
    UserTenantId = 'UserTenantId',
    IsSharedUXDarkModeOn = 'isSharedUXDarkModeOn',
    HostAppName = 'HostAppName',
    IsRecordingEventCaptureEnabled = 'IsRecordingEventCaptureEnabled',
    EnableFlightAuthVersion = 'EnableFlightAuthVersion',
    AssociatedOneNotePageId = 'AssociatedOneNotePageId',
    AssociatedOneNoteNotebookId = 'AssociatedOneNoteNotebookId',
    AssociatedOneNoteSectionId = 'AssociatedOneNoteSectionId',
    IsTranscriptionNewFlowEnabled = 'IsTranscriptionNewFlowEnabled',
    NotifyTranscriptionEventToHostAppEnabled = 'NotifyTranscriptionEventToHostAppEnabled',
    IsMultiTranscriptionStorageSupported = 'IsMultiTranscriptionStorageSupported',
    MultiTranscriptStorageLimitPerPage = 'MultiTranscriptStorageLimitPerPage',
    ShouldShowOneDriveIdentityInfoDialog = 'ShouldShowOneDriveIdentityInfoDialog',
    IsAssociatedPageDeleted = 'IsAssociatedPageDeleted',
    AddTranscriptionIndicatorToHostEnabled = 'AddTranscriptionIndicatorToHostEnabled',
    OneNoteNotebookIdentityEmail = 'OneNoteNotebookIdentityEmail',
    AssociatedOneNotePageHyperLink = 'AssociatedOneNotePageHyperLink',
    PageSwitchLiveRecordingIndicatorEnabled = 'PageSwitchLiveRecordingIndicatorEnabled',
    LinkedPageNavigationDuringRecordingEnabled = 'LinkedPageNavigationDuringRecordingEnabled',
    IsTranscriptionFetchedFromCustomProperties = 'IsTranscriptionFetchedFromCustomProperties',
    WordDocumentIdentityEmail = 'WordDocumentIdentityEmail',
    SDXPackageVersion = 'SDXPackageVersion',
    Audience = 'Audience',
    DocumentStorageType = 'DocumentStorageType',
    IsAuthDelayParameterEnabled = 'IsAuthDelayParameterEnabled'
};

/**
 * Is a wrapper around the settings that are passed in to the SDX from the hosting app
 */
export class HostAppSettings {
    /**
     * Returns a string parsed from reading the setting
     * @param settingName  Name of setting to read
     */
    public static GetSettingAsNumber(settingName: HostAppSettingName): number {
      let settingVal = Office.context.document.settings.get(settingName.toString());
      if (settingVal === null) {
        return 0;
      }
      return ((typeof settingVal === "number" || settingVal === undefined) ? settingVal : parseInt(settingVal.toString()));
    }

    /**
     * Returns a string parsed from reading the setting
     * @param settingName  Name of setting to read
     */
    public static GetSettingAsString(settingName: HostAppSettingName): string {
      let settingVal = Office.context.document.settings.get(settingName.toString());
      return ((settingVal === null || settingVal === undefined) ? settingVal : settingVal.toString());
    }

    /**
     * Returns an object associated with passed setting
     * @param settingName  Name of setting to read
     */
    public static GetSetting(settingName: HostAppSettingName): any {
      return Office.context.document.settings.get(settingName.toString());
    }

    /**
     * Sets a setting in the in-memory settings bag
     * @param settingName  Name of setting to set
     * @param value Value to set to
     */
    public static Set(settingName: HostAppSettingName, value: any): void {
        Office.context.document.settings.set(settingName,
            typeof value == "string"? value: JSON.stringify(value));
    }

    /**
     * Returns false if setting doesn't exist
     * @param settingName Name of setting to read
     */
    public static IsSettingTrue(settingName: HostAppSettingName): boolean {
        const settingValue: string = this.GetSettingAsString(settingName);
        if (settingValue) {
            return (settingValue.toLowerCase() == 'true');
        } else {
            return false;
        }
    }
};

export function GetInitialMonthlyMinutesUsed(): number {
    if (IS_DEV_BUILD)
        return 42;

    return HostAppSettings.GetSettingAsNumber(HostAppSettingName.MonthlyMinutesUsed);
}

export function GetSSOLoginUrl(): string {
  if (IS_DEV_BUILD)
      return DEV_SSO_LOGIN_URL;

  return HostAppSettings.GetSettingAsString(HostAppSettingName.Audience) == "DOGFOOD" || HostAppSettings.GetSettingAsString(HostAppSettingName.Audience) == "MICROSOFT" ? DOGFOOD_SSO_LOGIN_URL : PROD_SSO_LOGIN_URL;
}

export function GetFrontDoorBaseUrl(): string {
    if (IS_DEV_BUILD)
      return DEV_FRONTDOOR_BASE_URL;

    let frontdoorURL = HostAppSettings.GetSettingAsString(HostAppSettingName.FrontDoorBaseUrl);

    if (frontdoorURL.startsWith("wss:")) {
      frontdoorURL = frontdoorURL.replace("wss:", "https:");
    }

    if (!frontdoorURL.startsWith("https:")) {
      frontdoorURL = 'https://' + frontdoorURL;
    }

    if (!frontdoorURL.endsWith("/SpeechHandler.ashx")) {
      frontdoorURL = frontdoorURL + '/SpeechHandler.ashx';
    }

    return frontdoorURL;
}

export function GetSupportedTranscriptionStorageType(): TranscriptionStorageType {
  const storageType = HostAppSettings.GetSettingAsString(HostAppSettingName.DocumentStorageType);
  switch (storageType) {
    case TranscriptionStorageType.DocumentXmlStorage:
      return TranscriptionStorageType.DocumentXmlStorage;
    case TranscriptionStorageType.DocumentStorage:
      return TranscriptionStorageType.DocumentStorage;
    case TranscriptionStorageType.UnKnownStorage:
      return TranscriptionStorageType.UnKnownStorage;
    default:
      return TranscriptionStorageType.DocumentStorage;
  }
}
